import React, { useEffect, useState } from 'react';
import { 
  PencilIcon,
  EyeIcon,
  TrashIcon, 
  PlusIcon, 
  ArrowUpIcon, 
  AdjustmentsHorizontalIcon, 
  ClockIcon,
  MagnifyingGlassIcon,
  CubeIcon,
  ExclamationTriangleIcon,
  BanknotesIcon,
  TagIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { useProductsPage, useInventorySummary, useCreateProduct, useUpdateProduct, useDeleteProduct } from '../../hooks/useInventory';
import { Product, CreateProductDto } from '../../services/inventory.service';
import ProductForm from '../../components/forms/ProductForm';
import { Pagination } from '../../components/ui/Pagination';
import AddStockModal from '../../components/modals/AddStockModal';
import StockAdjustmentModal from '../../components/modals/StockAdjustmentModal';
import StockMovementHistoryModal from '../../components/modals/StockMovementHistoryModal';
import ProductDetailsModal from '../../components/modals/ProductDetailsModal';
import { Modal } from '../../components/ui/Modal';
import { ConfirmDialog } from '../../components/ui/FormControls';
import { defaultRowsPerPage } from '../../lib/preferences';

type StockStatusKey = 'low' | 'normal' | 'high';

const STOCK_STATUS: Record<StockStatusKey, { status: string; color: string; bar: string }> = {
  low: { status: 'Low Stock', color: 'text-red-700 bg-red-50 ring-red-600/20', bar: 'bg-red-500' },
  normal: { status: 'Normal', color: 'text-green-700 bg-green-50 ring-green-600/20', bar: 'bg-green-500' },
  high: { status: 'High Stock', color: 'text-yellow-800 bg-yellow-50 ring-yellow-600/20', bar: 'bg-yellow-500' },
};

const getStockStatusKey = (product: Product): StockStatusKey => {
  if (Number(product.currentStock) <= Number(product.minimumStock)) return 'low';
  if (Number(product.currentStock) >= Number(product.maximumStock)) return 'high';
  return 'normal';
};

const formatNumber = (value: number) => Number(value || 0).toLocaleString('en-IN');

const formatCurrency = (value: number) =>
  `₹${Number(value || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const InventoryList: React.FC = () => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultRowsPerPage);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<StockStatusKey | ''>('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<Product | null>(null);
  const [viewProduct, setViewProduct] = useState<Product | null>(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  
  // Stock management modals
  const [addStockModal, setAddStockModal] = useState<{
    isOpen: boolean;
    product: Product | null;
  }>({ isOpen: false, product: null });
  
  const [adjustStockModal, setAdjustStockModal] = useState<{
    isOpen: boolean;
    product: Product | null;
  }>({ isOpen: false, product: null });
  
  const [historyModal, setHistoryModal] = useState<{
    isOpen: boolean;
    product: Product | null;
  }>({ isOpen: false, product: null });

  // Debounce search so the API is not called on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const { data: productsPage, isLoading, isFetching, error } = useProductsPage({
    page,
    limit: pageSize,
    search: debouncedSearch || undefined,
    category: categoryFilter || undefined,
    status: statusFilter || undefined,
  });
  const { data: summary, isLoading: isSummaryLoading } = useInventorySummary();

  const products = productsPage?.data ?? [];
  const total = productsPage?.total ?? 0;
  const totalPages = productsPage?.totalPages ?? 1;
  const categories = summary?.categories ?? [];

  // Step back when the current page no longer exists (e.g. after deleting its last row)
  useEffect(() => {
    if (productsPage && page > 1 && page > productsPage.totalPages) {
      setPage(Math.max(1, productsPage.totalPages));
    }
  }, [productsPage, page]);
  const createProductMutation = useCreateProduct();
  const updateProductMutation = useUpdateProduct();
  const deleteProductMutation = useDeleteProduct();

  const handleCreate = () => {
    setEditingProduct(null);
    setIsModalOpen(true);
  };

  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    setIsModalOpen(true);
  };

  const handleDelete = (product: Product) => {
    setDeleteConfirm(product);
  };

  const confirmDelete = async () => {
    if (deleteConfirm) {
      try {
        await deleteProductMutation.mutateAsync(deleteConfirm.id);
        setDeleteConfirm(null);
      } catch (error) {
        // Error handling is done in the hooks
      }
    }
  };

  const handleSubmit = async (data: CreateProductDto) => {
    try {
      if (editingProduct) {
        await updateProductMutation.mutateAsync({
          id: editingProduct.id,
          data,
        });
      } else {
        await createProductMutation.mutateAsync(data);
      }
      setIsModalOpen(false);
    } catch (error) {
      // Error handling is done in the hooks
    }
  };

  // Stock management handlers
  const handleAddStock = (product: Product) => {
    setAddStockModal({ isOpen: true, product });
  };

  const handleAdjustStock = (product: Product) => {
    setAdjustStockModal({ isOpen: true, product });
  };

  const handleView = (product: Product) => {
    setViewProduct(product);
    setIsViewOpen(true);
  };

  const handleViewHistory = (product: Product) => {
    setHistoryModal({ isOpen: true, product });
  };

  const hasFilters = !!(search || categoryFilter || statusFilter);

  const clearFilters = () => {
    setSearch('');
    setCategoryFilter('');
    setStatusFilter('');
    setPage(1);
  };

  const stats = [
    { label: 'Total Products', value: formatNumber(summary?.totalProducts ?? 0), icon: CubeIcon, tone: 'text-indigo-600 bg-indigo-50' },
    { label: 'Low Stock Items', value: formatNumber(summary?.lowStock ?? 0), icon: ExclamationTriangleIcon, tone: 'text-red-600 bg-red-50' },
    { label: 'Stock Value (at cost)', value: formatCurrency(summary?.stockValue ?? 0), icon: BanknotesIcon, tone: 'text-green-600 bg-green-50' },
    { label: 'Categories', value: formatNumber(categories.length), icon: TagIcon, tone: 'text-purple-600 bg-purple-50' },
  ];

  const selectClass =
    'h-10 rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-700 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500';
  const thClass = 'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500';

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Error loading products</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Inventory</h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage your inventory and stock levels
          </p>
        </div>
        <button
          type="button"
          onClick={handleCreate}
          className="inline-flex items-center justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Add Product
        </button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {stats.map((stat) => (
          <div
            key={stat.label}
            className="flex items-center gap-4 rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200"
          >
            <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${stat.tone}`}>
              <stat.icon className="h-6 w-6" />
            </div>
            <div className="min-w-0">
              <p className="text-sm text-gray-500">{stat.label}</p>
              <p className="truncate text-xl font-semibold text-gray-900">
                {isSummaryLoading ? '—' : stat.value}
              </p>
            </div>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
        {/* Search & filters */}
        <div className="flex flex-col gap-3 border-b border-gray-200 p-4 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search products by name, SKU, or category..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="block h-10 w-full rounded-md border border-gray-300 bg-white pl-10 pr-3 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <select
              aria-label="Filter by category"
              value={categoryFilter}
              onChange={(e) => {
                setCategoryFilter(e.target.value);
                setPage(1);
              }}
              className={selectClass}
            >
              <option value="">All categories</option>
              {categories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
            <select
              aria-label="Filter by stock status"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as StockStatusKey | '');
                setPage(1);
              }}
              className={selectClass}
            >
              <option value="">All statuses</option>
              <option value="low">Low Stock</option>
              <option value="normal">Normal</option>
              <option value="high">High Stock</option>
            </select>
            {hasFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="inline-flex h-10 items-center rounded-md px-2 text-sm font-medium text-gray-600 hover:text-gray-900"
              >
                <XMarkIcon className="h-4 w-4 mr-1" />
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Products Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className={thClass}>Product</th>
                <th className={thClass}>SKU</th>
                <th className={thClass}>Category</th>
                <th className={thClass}>Stock</th>
                <th className={thClass}>Unit</th>
                <th className={`${thClass} text-right`}>Price</th>
                <th className={thClass}>Status</th>
                <th className={`${thClass} text-right`}>Actions</th>
              </tr>
            </thead>
            <tbody className={`divide-y divide-gray-100 bg-white ${isFetching && !isLoading ? 'opacity-60' : ''}`}>
              {isLoading ? (
                <tr>
                  <td className="px-4 py-12 text-sm text-gray-500 text-center" colSpan={8}>
                    Loading products...
                  </td>
                </tr>
              ) : products.length === 0 ? (
                <tr>
                  <td className="px-4 py-12 text-center" colSpan={8}>
                    <CubeIcon className="mx-auto h-10 w-10 text-gray-300" />
                    <p className="mt-2 text-sm font-medium text-gray-900">No products found</p>
                    <p className="mt-1 text-sm text-gray-500">
                      {hasFilters
                        ? 'Try changing or clearing the search and filters.'
                        : 'Add your first product to start tracking stock.'}
                    </p>
                  </td>
                </tr>
              ) : (
                products.map((product) => {
                  const stockStatus = STOCK_STATUS[getStockStatusKey(product)];
                  const maximumStock = Number(product.maximumStock) || 0;
                  const stockPercent =
                    maximumStock > 0
                      ? Math.min(100, Math.max(0, (Number(product.currentStock) / maximumStock) * 100))
                      : 0;
                  return (
                    <tr key={product.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm">
                        <div className="font-medium text-gray-900">{product.name}</div>
                        {product.description && (
                          <div className="text-gray-500 truncate max-w-md">
                            {product.description}
                          </div>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm">
                        <span className="rounded bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-700">
                          {product.sku}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">{product.category}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm">
                        <div className="w-36">
                          <div className="text-gray-900">
                            <span className="font-medium">{formatNumber(product.currentStock)}</span>
                            <span className="text-gray-500"> / {formatNumber(product.maximumStock)}</span>
                          </div>
                          <div className="mt-1 h-1.5 w-full rounded-full bg-gray-100">
                            <div
                              className={`h-1.5 rounded-full ${stockStatus.bar}`}
                              style={{ width: `${stockPercent}%` }}
                            />
                          </div>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">{product.unit}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-right font-medium text-gray-900 tabular-nums">
                        {formatCurrency(product.price)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm">
                        <span
                          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${stockStatus.color}`}
                        >
                          {stockStatus.status}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-medium">
                        <div className="flex justify-end space-x-1">
                          <button
                            onClick={() => handleAddStock(product)}
                            className="text-green-600 hover:text-green-900 p-1.5 rounded hover:bg-green-50"
                            title="Add Stock"
                            aria-label="Add Stock"
                          >
                            <ArrowUpIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleAdjustStock(product)}
                            className="text-blue-600 hover:text-blue-900 p-1.5 rounded hover:bg-blue-50"
                            title="Adjust Stock"
                            aria-label="Adjust Stock"
                          >
                            <AdjustmentsHorizontalIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleViewHistory(product)}
                            className="text-purple-600 hover:text-purple-900 p-1.5 rounded hover:bg-purple-50"
                            title="View History"
                            aria-label="View History"
                          >
                            <ClockIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleView(product)}
                            className="text-gray-600 hover:text-gray-900 p-1.5 rounded hover:bg-gray-100"
                            title="View Details"
                            aria-label="View Details"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleEdit(product)}
                            className="text-indigo-600 hover:text-indigo-900 p-1.5 rounded hover:bg-indigo-50"
                            title="Edit Product"
                            aria-label="Edit Product"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(product)}
                            className="text-red-600 hover:text-red-900 p-1.5 rounded hover:bg-red-50"
                            title="Delete Product"
                            aria-label="Delete Product"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {!isLoading && total > 0 && (
          <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 lg:flex-row lg:items-center lg:gap-6">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-gray-500">
              <span>
                Showing {formatNumber((page - 1) * pageSize + 1)}–
                {formatNumber(Math.min(page * pageSize, total))} of {formatNumber(total)} products
              </span>
              <label className="flex items-center gap-2">
                Rows per page
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                  className="h-8 rounded-md border border-gray-300 bg-white px-2 text-sm text-gray-700 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <Pagination
              className="lg:flex-1"
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          </div>
        )}
      </div>

      {/* Product Form Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingProduct ? 'Edit Product' : 'Add New Product'}
        description={
          editingProduct
            ? 'Update the product details, pricing and stock levels.'
            : 'Enter the product details, pricing and opening stock.'
        }
        size="xl"
      >
        <ProductForm
          key={editingProduct?.id || 'new'}
          product={editingProduct || undefined}
          categories={categories}
          onSubmit={handleSubmit}
          isLoading={createProductMutation.isPending || updateProductMutation.isPending}
          onCancel={() => setIsModalOpen(false)}
        />
      </Modal>

      {/* Product Details Modal */}
      <ProductDetailsModal
        isOpen={isViewOpen}
        onClose={() => setIsViewOpen(false)}
        product={viewProduct}
        onEdit={(product) => {
          setIsViewOpen(false);
          handleEdit(product);
        }}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmDialog
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={confirmDelete}
        title="Delete Product"
        message={
          <>
            <span className="font-medium text-gray-900">{deleteConfirm?.name}</span> will be removed
            from inventory. This cannot be undone.
          </>
        }
        confirmLabel="Delete"
        pendingLabel="Deleting..."
        isPending={deleteProductMutation.isPending}
      />

      {/* Stock Management Modals */}
      <AddStockModal
        isOpen={addStockModal.isOpen}
        onClose={() => setAddStockModal({ isOpen: false, product: null })}
        product={addStockModal.product}
      />

      <StockAdjustmentModal
        isOpen={adjustStockModal.isOpen}
        onClose={() => setAdjustStockModal({ isOpen: false, product: null })}
        product={adjustStockModal.product}
      />

      <StockMovementHistoryModal
        isOpen={historyModal.isOpen}
        onClose={() => setHistoryModal({ isOpen: false, product: null })}
        product={historyModal.product}
      />
    </div>
  );
};

export default InventoryList;